import Fuse, { IFuseOptions, FuseResult, FuseResultMatch } from 'fuse.js';
import { GenericItem } from '../components/ContentLibrary/ItemBrowser';

// Configuration for Fuse.js search
export const createFuseInstance = (items: GenericItem[]) => {
  const options: IFuseOptions<GenericItem> = {
    // Fuzzy search configuration
    threshold: 0.4, // 0.0 = perfect match, 1.0 = match anything
    distance: 100, // maximum distance characters can be from expected position
    minMatchCharLength: 2, // minimum character length for matching
    
    // Include item score and matched indices for highlighting
    includeScore: true,
    includeMatches: true,
    
    // Fields to search with different weights
    keys: [
      {
        name: 'name',
        weight: 0.4 // Highest priority for name matches
      },
      {
        name: 'description',
        weight: 0.2 // Medium priority for description
      },
      {
        name: 'tags',
        weight: 0.2 // Medium priority for tags
      },
      {
        name: 'domain',
        weight: 0.1 // Lower priority for domain (spells)
      },
      {
        name: 'type',
        weight: 0.1 // Lower priority for type
      }
    ]
  };

  return new Fuse(items, options);
};

// Helper function to extract search results with scores
export interface SearchResult {
  item: GenericItem;
  score: number;
  matches: FuseResultMatch[];
}

export const performFuzzySearch = (
  fuse: Fuse<GenericItem>, 
  searchTerm: string,
  maxResults = 50
): SearchResult[] => {
  if (!searchTerm.trim()) {
    return [];
  }

  const results = fuse.search(searchTerm, { limit: maxResults });
  
  return results.map(result => ({
    item: result.item,
    score: result.score || 1,
    matches: (result.matches || []) as FuseResultMatch[]
  }));
};

// Helper function for highlighting matches in search results
export const highlightMatches = (
  text: string, 
  matches: FuseResultMatch[], 
  fieldName: string
): string => {
  const fieldMatch = matches.find(match => match.key === fieldName);
  
  if (!fieldMatch || !fieldMatch.indices) {
    return text;
  }

  let highlightedText = '';
  let lastIndex = 0;

  // Sort indices by start position
  const sortedIndices = [...fieldMatch.indices].sort((a: [number, number], b: [number, number]) => a[0] - b[0]);

  for (const [start, end] of sortedIndices) {
    // Add text before the match
    highlightedText += text.slice(lastIndex, start);
    
    // Add highlighted match
    highlightedText += `<mark class="search-highlight">${text.slice(start, end + 1)}</mark>`;
    
    lastIndex = end + 1;
  }

  // Add remaining text
  highlightedText += text.slice(lastIndex);

  return highlightedText;
};